import { useState } from "react";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { useToast } from "@/components/ui/toast";
import { errorMessage } from "@/lib/api";
import {
  useAdminCreateAthlete,
  useAdminUpdateAthlete,
} from "@/features/admin/hooks/useAdminData";
import type { Athlete, AthleteUpdate } from "@/lib/types";
import { FIELD_CLASS, FIELD_LABEL_CLASS, parseBirthYear } from "../athleteDisplay";

/**
 * Create / edit one athlete (#908): display name (required), given and
 * family names, the optional birth year (D11 — a year, never a date) and,
 * for an editor the API flagged as a club admin (`can_merge`), the
 * active / archived status. `merged` is set only by the merge path, so a
 * merged athlete never shows the status control.
 */
export function AthleteComposerDialog({
  mode,
  athlete,
  canSetStatus = false,
  onClose,
  onSaved,
}: {
  mode: "create" | "edit";
  athlete?: Athlete;
  canSetStatus?: boolean;
  onClose: () => void;
  onSaved: (athlete: Athlete) => void;
}) {
  const toast = useToast();
  const [displayName, setDisplayName] = useState(athlete?.display_name ?? "");
  const [givenName, setGivenName] = useState(athlete?.given_name ?? "");
  const [familyName, setFamilyName] = useState(athlete?.family_name ?? "");
  const [birthYear, setBirthYear] = useState(
    athlete?.birth_year ? String(athlete.birth_year) : "",
  );
  const [status, setStatus] = useState<"active" | "archived">(
    athlete?.status === "archived" ? "archived" : "active",
  );
  const [error, setError] = useState<string | null>(null);
  const createMut = useAdminCreateAthlete();
  const updateMut = useAdminUpdateAthlete();
  const pending = createMut.isPending || updateMut.isPending;
  const showStatus =
    mode === "edit" && canSetStatus && athlete?.status !== "merged";

  const submit = async () => {
    setError(null);
    const name = displayName.trim();
    if (!name) {
      setError("Display name is required.");
      return;
    }
    const year = parseBirthYear(birthYear);
    if (year === undefined) {
      setError("Birth year must be a four-digit year between 1900 and 2100.");
      return;
    }
    const names = {
      display_name: name,
      given_name: givenName.trim() || null,
      family_name: familyName.trim() || null,
      birth_year: year,
    };
    try {
      let saved: Athlete;
      if (mode === "create") {
        saved = await createMut.mutateAsync(names);
      } else {
        const body: AthleteUpdate = { ...names };
        if (showStatus && status !== athlete!.status) body.status = status;
        saved = await updateMut.mutateAsync({ athleteId: athlete!.id, body });
      }
      toast.success(mode === "create" ? "Athlete created" : "Athlete updated");
      onSaved(saved);
    } catch (err) {
      const message = errorMessage(err);
      setError(message);
      toast.error(
        mode === "create" ? "Couldn't create athlete" : "Couldn't update athlete",
        { description: message },
      );
    }
  };

  return (
    <ConfirmDialog
      title={mode === "create" ? "New athlete" : "Edit athlete"}
      confirmDisabled={pending}
      message={
        <div className="flex flex-col gap-2">
          <label className="flex flex-col gap-1">
            <span className={FIELD_LABEL_CLASS}>
              Display name <strong aria-hidden>*</strong>
            </span>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              autoFocus
              className={FIELD_CLASS}
              data-testid="admin-athlete-composer-name"
            />
          </label>
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="flex flex-col gap-1">
              <span className={FIELD_LABEL_CLASS}>Given name</span>
              <input
                type="text"
                value={givenName}
                onChange={(e) => setGivenName(e.target.value)}
                className={FIELD_CLASS}
                data-testid="admin-athlete-composer-given-name"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className={FIELD_LABEL_CLASS}>Family name</span>
              <input
                type="text"
                value={familyName}
                onChange={(e) => setFamilyName(e.target.value)}
                className={FIELD_CLASS}
                data-testid="admin-athlete-composer-family-name"
              />
            </label>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="flex flex-col gap-1">
              <span className={FIELD_LABEL_CLASS}>Birth year</span>
              <input
                type="number"
                inputMode="numeric"
                min={1900}
                max={2100}
                step={1}
                placeholder="YYYY"
                value={birthYear}
                onChange={(e) => setBirthYear(e.target.value)}
                className={FIELD_CLASS}
                data-testid="admin-athlete-composer-birth-year"
              />
            </label>
            {showStatus ? (
              <label className="flex flex-col gap-1">
                <span className={FIELD_LABEL_CLASS}>Status</span>
                <TkticSelect
                  value={status}
                  onChange={(next) =>
                    setStatus(next === "archived" ? "archived" : "active")
                  }
                  options={[
                    { value: "active", label: "Active" },
                    { value: "archived", label: "Archived" },
                  ]}
                  size="sm"
                  ariaLabel="Athlete status"
                  data-testid="admin-athlete-composer-status"
                />
              </label>
            ) : null}
          </div>
          <p className="text-[0.65rem] text-ink-muted">
            The birth year is a year only and is shown to club admins and the
            athlete&rsquo;s own team staff.
          </p>
          {error ? (
            <p
              data-testid="admin-athlete-composer-error"
              className="font-mono text-xs text-destructive"
            >
              {error}
            </p>
          ) : null}
        </div>
      }
      confirmLabel={
        pending
          ? "Saving…"
          : mode === "create"
            ? "Create athlete"
            : "Save changes"
      }
      onConfirm={() => void submit()}
      onClose={onClose}
      data-testid="admin-athlete-composer"
    />
  );
}
